/**
 * Attribution capture. Mirrors packages/analytics/src/analytics.ts semantics
 * with no posthog-js dependency. Reads UTMs, gclid, fbc/fbp, gbraid/wbraid,
 * msclkid, session_id, landing_url from URL/cookie/localStorage.
 */

import { uuidv4 } from './uuid';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const UTM_STORAGE_KEY = 'ign_pixel_utm';
const SESSION_STORAGE_KEY = 'ign_pixel_session';
const LANDING_STORAGE_KEY = 'ign_pixel_landing_url';
const CLICK_ID_STORAGE_KEY = 'ign_pixel_clicks';
const AD_ID_STORAGE_KEY = 'ign_pixel_ad_ids';
const INITIAL_REFERRER_STORAGE_KEY = 'ign_pixel_initial_referrer';

// Length caps mirror server-side limits. Over-limit values are dropped.
const GCLID_MAX = 2048;
const GA4_CID_MAX = 100;
const POSTHOG_ID_MAX = 200;
const FBC_FBP_MAX = 512;
const EVENT_ID_MAX = 100;
const GBRAID_MAX = 255;
const WBRAID_MAX = 255;
const MSCLKID_MAX = 255;
const AD_ID_MAX = 255;
const REFERRER_MAX = 2048;
const SEARCH_ENGINE_MAX = 50;
const SEARCH_QUERY_MAX = 500;
// Mirrors the CHECK on marketing.form_submissions.posthog_session_id in
// migration 112. Numerically equal to POSTHOG_ID_MAX today, but kept
// separate so a future change to either backend column's bound doesn't
// silently rebind the other on the pixel side.
const POSTHOG_SESSION_ID_MAX = 200;

const AD_ID_KEYS = ['campaign_id', 'medium_id', 'content_id', 'ad_id'] as const;
export type AdIdKey = (typeof AD_ID_KEYS)[number];
export type AdIds = Partial<Record<AdIdKey, string>>;

export interface AttributionSnapshot {
  utm: UtmParams;
  gclid?: string;
  fbc?: string;
  fbp?: string;
  gbraid?: string;
  wbraid?: string;
  msclkid?: string;
  adIds?: AdIds;
  sessionId?: string;
  landingUrl?: string;
  pageUrl?: string;
  referrer?: string;
  // First-touch external referrer captured on first pageview and persisted
  // for the visitor lifetime. Survives internal navigation that would
  // otherwise overwrite document.referrer with the prior internal page.
  initialReferrer?: string;
  initialSearchEngine?: string;
  initialSearchQuery?: string;
}

function safeGetSearch(): URLSearchParams | undefined {
  try {
    if (typeof window === 'undefined' || !window.location) return undefined;
    return new URLSearchParams(window.location.search);
  } catch {
    return undefined;
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const escaped = name.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
  return m ? m[1] : undefined;
}

function readLocalStorage(key: string): string | undefined {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    const v = localStorage.getItem(key);
    return v === null ? undefined : v;
  } catch {
    return undefined;
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, value);
  } catch {
    /* storage blocked or quota exceeded */
  }
}

function decode(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    return decodeURIComponent(value);
  } catch {
    return undefined;
  }
}

// ─── UTM ───

function readUrlUtms(): UtmParams {
  const params = safeGetSearch();
  if (!params) return {};
  const out: UtmParams = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) out[key] = v;
  }
  return out;
}

function readStoredUtms(): UtmParams {
  const raw = readLocalStorage(UTM_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: UtmParams = {};
    for (const key of UTM_KEYS) {
      const v = (parsed as Record<string, unknown>)[key];
      if (typeof v === 'string' && v) out[key] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function readCookieUtms(): UtmParams {
  const raw = readCookie('ign_utm');
  if (!raw) return {};
  const decoded = decode(raw);
  if (!decoded) return {};
  try {
    const parsed: unknown = JSON.parse(decoded);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: UtmParams = {};
    for (const key of UTM_KEYS) {
      const v = (parsed as Record<string, unknown>)[key];
      if (typeof v === 'string' && v) out[key] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Resolve UTMs with first-touch precedence:
 *   1. ign_utm cross-domain cookie
 *   2. localStorage (ign_pixel_utm)
 *   3. current URL params
 * Persists merged result into localStorage so first-touch attribution
 * survives navigation that drops the URL params.
 */
const UTM_MAX = 255;

export function resolveUtms(): UtmParams {
  const url = readUrlUtms();
  const stored = readStoredUtms();
  const cookie = readCookieUtms();
  // earliest source wins: cookie > stored > URL
  const merged: UtmParams = { ...url, ...stored, ...cookie };
  for (const key of UTM_KEYS) {
    if (merged[key] && merged[key].length > UTM_MAX) {
      delete merged[key];
    }
  }
  if (Object.keys(merged).length > 0) {
    try {
      writeLocalStorage(UTM_STORAGE_KEY, JSON.stringify(merged));
    } catch {
      /* noop */
    }
  }
  return merged;
}

// ─── Click IDs ───

export interface ClickIds {
  gclid?: string;
  fbc?: string;
  fbp?: string;
  gbraid?: string;
  wbraid?: string;
  msclkid?: string;
}

/**
 * Resolve click IDs with first-touch precedence.
 * Reads fresh values from URL params / cookies, merges with stored values
 * (stored wins — earliest captured is never overwritten), persists merged
 * result, and returns it.
 *
 * Call at init time so click IDs are persisted to localStorage immediately
 * on landing page load, before navigation can drop URL params.
 */
export function resolveClickIds(): ClickIds {
  // Read fresh values from their current sources
  const fresh: ClickIds = {};
  const freshGclid = getGclid();
  if (freshGclid) fresh.gclid = freshGclid;
  const meta = getMetaContext();
  if (meta.fbc) fresh.fbc = meta.fbc;
  if (meta.fbp) fresh.fbp = meta.fbp;
  const freshGbraid = getGbraid();
  if (freshGbraid) fresh.gbraid = capLength(freshGbraid, GBRAID_MAX);
  const freshWbraid = getWbraid();
  if (freshWbraid) fresh.wbraid = capLength(freshWbraid, WBRAID_MAX);
  const freshMsclkid = getMsclkid();
  if (freshMsclkid) fresh.msclkid = capLength(freshMsclkid, MSCLKID_MAX);

  // Read stored values
  const stored = readStoredClickIds();

  // Merge with first-touch precedence: stored wins over fresh
  const merged: ClickIds = { ...fresh, ...stored };

  // Cap values before storage
  const capped: ClickIds = {};
  if (merged.gclid) capped.gclid = capLength(merged.gclid, GCLID_MAX);
  if (merged.fbc) capped.fbc = capLength(merged.fbc, FBC_FBP_MAX);
  if (merged.fbp) capped.fbp = capLength(merged.fbp, FBC_FBP_MAX);
  if (merged.gbraid) capped.gbraid = capLength(merged.gbraid, GBRAID_MAX);
  if (merged.wbraid) capped.wbraid = capLength(merged.wbraid, WBRAID_MAX);
  if (merged.msclkid) capped.msclkid = capLength(merged.msclkid, MSCLKID_MAX);

  // Write merged result to localStorage
  if (Object.keys(capped).length > 0) {
    try {
      writeLocalStorage(CLICK_ID_STORAGE_KEY, JSON.stringify(capped));
    } catch {
      /* noop */
    }
  }

  // fbc stored here is first-touch. Server-side senders must re-read the
  // live _fbc cookie for browser event dedup — do NOT use this persisted
  // value for outbound conversion events.
  return capped;
}

function readStoredClickIds(): ClickIds {
  const raw = readLocalStorage(CLICK_ID_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: ClickIds = {};
    const fields: (keyof ClickIds)[] = ['gclid', 'fbc', 'fbp', 'gbraid', 'wbraid', 'msclkid'];
    for (const key of fields) {
      const v = (parsed as Record<string, unknown>)[key];
      if (typeof v === 'string' && v) out[key] = v;
    }
    return out;
  } catch {
    return {};
  }
}

// ─── Ad Platform IDs ───

export function resolveAdIds(): AdIds {
  const fresh: AdIds = {};
  const params = safeGetSearch();
  if (params) {
    for (const key of AD_ID_KEYS) {
      const v = params.get(key);
      if (v) fresh[key] = capLength(v, AD_ID_MAX) ?? undefined;
    }
  }

  const stored = readStoredAdIds();
  const merged: AdIds = { ...fresh, ...stored };

  const capped: AdIds = {};
  for (const key of AD_ID_KEYS) {
    if (merged[key]) capped[key] = capLength(merged[key], AD_ID_MAX) ?? undefined;
  }

  if (Object.keys(capped).length > 0) {
    try {
      writeLocalStorage(AD_ID_STORAGE_KEY, JSON.stringify(capped));
    } catch {
      /* noop */
    }
  }

  return capped;
}

function readStoredAdIds(): AdIds {
  const raw = readLocalStorage(AD_ID_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: AdIds = {};
    for (const key of AD_ID_KEYS) {
      const v = (parsed as Record<string, unknown>)[key];
      if (typeof v === 'string' && v) {
        const capped = capLength(v, AD_ID_MAX);
        if (capped) out[key] = capped;
      }
    }
    return out;
  } catch {
    return {};
  }
}

// ─── gclid ───

export function getGclid(): string | undefined {
  const params = safeGetSearch();
  const fromUrl = params ? params.get('gclid') : null;
  if (fromUrl) return capLength(fromUrl, GCLID_MAX);
  const raw = readCookie('_gcl_aw');
  if (!raw) return undefined;
  const decoded = decode(raw);
  if (!decoded) return undefined;
  const parts = decoded.split('.');
  const value = parts.length >= 3 ? parts.slice(2).join('.') : decoded;
  return capLength(value, GCLID_MAX);
}

// ─── Meta ───

export interface MetaContext {
  fbc?: string;
  fbp?: string;
}

export function getMetaContext(): MetaContext {
  const out: MetaContext = {};
  const fbcRaw = readCookie('_fbc');
  const fbpRaw = readCookie('_fbp');
  if (fbcRaw) {
    const v = decode(fbcRaw);
    if (v) out.fbc = capLength(v, FBC_FBP_MAX);
  }
  if (fbpRaw) {
    const v = decode(fbpRaw);
    if (v) out.fbp = capLength(v, FBC_FBP_MAX);
  }
  // Synthesize fbc from fbclid when cookie absent.
  if (!out.fbc) {
    const params = safeGetSearch();
    const fbclid = params ? params.get('fbclid') : null;
    if (fbclid) {
      const clean = fbclid.replace(/^fb\.1\.\d+\./, '').replace(/^fb\.1\./, '');
      if (clean) out.fbc = capLength('fb.1.' + Date.now() + '.' + clean, FBC_FBP_MAX);
    }
  }
  return out;
}

export function hasMetaAttribution(): boolean {
  if (typeof document === 'undefined') return false;
  if (/(?:^|;\s*)_fbc=/.test(document.cookie)) return true;
  if (/(?:^|;\s*)_fbp=/.test(document.cookie)) return true;
  const params = safeGetSearch();
  return params ? params.has('fbclid') : false;
}

// ─── Session / landing url ───

export function getOrCreateSessionId(): string {
  const existing = readLocalStorage(SESSION_STORAGE_KEY);
  if (existing) return existing;
  const id = uuidv4();
  writeLocalStorage(SESSION_STORAGE_KEY, id);
  return id;
}

export function getOrCreateLandingUrl(): string | undefined {
  const existing = readLocalStorage(LANDING_STORAGE_KEY);
  if (existing) return existing;
  // Sanitize to origin+pathname so query/fragment PII (magic-link tokens,
  // ?email=, ?token=) never persists in localStorage or ships to the
  // server. UTMs and click IDs are captured into dedicated fields above.
  const sanitized = safePageUrl();
  if (!sanitized) return undefined;
  writeLocalStorage(LANDING_STORAGE_KEY, sanitized);
  return sanitized;
}

// ─── Misc click IDs ───

function urlParam(name: string): string | undefined {
  const params = safeGetSearch();
  const v = params ? params.get(name) : null;
  return v || undefined;
}

export function getGbraid(): string | undefined {
  return urlParam('gbraid');
}

export function getWbraid(): string | undefined {
  return urlParam('wbraid');
}

export function getMsclkid(): string | undefined {
  return urlParam('msclkid');
}

// ─── page url / referrer ───

export function safePageUrl(): string | undefined {
  if (typeof window === 'undefined' || !window.location) return undefined;
  try {
    const u = new URL(window.location.href);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return undefined;
    return u.origin + u.pathname;
  } catch {
    return undefined;
  }
}

export function safeReferrer(): string | undefined {
  if (typeof document === 'undefined' || !document.referrer) return undefined;
  try {
    const u = new URL(document.referrer);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return undefined;
    return u.origin + u.pathname;
  } catch {
    return undefined;
  }
}

/**
 * Returns true if `raw` is a valid external-origin http(s) URL bounded by
 * REFERRER_MAX. Used to validate BOTH fresh document.referrer captures and
 * any value already sitting in localStorage on read — the stored slot is
 * mutable across tabs / extensions / injected scripts, so a hostile
 * `javascript:alert(1)` or oversized blob could survive prior writes
 * even though our write path is bounded.
 */
function isValidExternalReferrer(raw: string): boolean {
  if (raw.length > REFERRER_MAX) return false;
  if (typeof window === 'undefined' || !window.location) return false;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return false;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
  if (u.host.toLowerCase() === window.location.host.toLowerCase()) return false;
  return true;
}

/**
 * Returns the FULL external referrer (origin + path + query) once, on the
 * first pageview where document.referrer points to a different origin than
 * the current host. Persisted to localStorage so internal navigation does
 * not overwrite it. Search engines often strip ?q= but when present we want
 * it — that's the whole reason we keep the query string here vs. safeReferrer.
 *
 * On read we re-validate the stored value (protocol, length, cross-origin)
 * and drop the key if it fails. localStorage is mutable across tabs and
 * survives extension tampering, so trusting an existing value verbatim
 * could let an attacker-controlled `javascript:...` URL or oversized blob
 * propagate into hidden inputs and submissions.
 */
export function getOrCreateInitialReferrer(): string | undefined {
  const existing = readLocalStorage(INITIAL_REFERRER_STORAGE_KEY);
  if (existing) {
    if (isValidExternalReferrer(existing)) return existing;
    // Stored value is invalid (wrong protocol, oversized, same-origin, or
    // unparseable). Drop it and fall through to recapture from
    // document.referrer; the fresh capture goes through the same validator
    // below before write.
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(INITIAL_REFERRER_STORAGE_KEY);
      }
    } catch {
      /* storage blocked */
    }
  }
  if (typeof document === 'undefined' || !document.referrer) return undefined;
  if (!isValidExternalReferrer(document.referrer)) return undefined;
  // Validator passed — safe to materialize.
  const full = new URL(document.referrer).toString();
  writeLocalStorage(INITIAL_REFERRER_STORAGE_KEY, full);
  return full;
}

interface SearchEngineMatch {
  engine: string;
  queryParam: string | null;
}

const SEARCH_ENGINES: ReadonlyArray<{ host: RegExp; engine: string; queryParam: string | null }> = [
  { host: /(^|\.)google\./i, engine: 'google', queryParam: 'q' },
  { host: /(^|\.)bing\./i, engine: 'bing', queryParam: 'q' },
  { host: /(^|\.)duckduckgo\./i, engine: 'duckduckgo', queryParam: 'q' },
  { host: /(^|\.)yahoo\./i, engine: 'yahoo', queryParam: 'p' },
  { host: /(^|\.)yandex\./i, engine: 'yandex', queryParam: 'text' },
  { host: /(^|\.)baidu\./i, engine: 'baidu', queryParam: 'wd' },
  { host: /(^|\.)ecosia\./i, engine: 'ecosia', queryParam: 'q' },
  { host: /(^|\.)brave\./i, engine: 'brave', queryParam: 'q' },
  { host: /(^|\.)kagi\./i, engine: 'kagi', queryParam: 'q' },
  { host: /(^|\.)startpage\./i, engine: 'startpage', queryParam: 'query' },
];

function classifySearchEngine(referrerUrl: string): SearchEngineMatch | undefined {
  let u: URL;
  try {
    u = new URL(referrerUrl);
  } catch {
    return undefined;
  }
  for (const e of SEARCH_ENGINES) {
    if (e.host.test(u.hostname)) return { engine: e.engine, queryParam: e.queryParam };
  }
  return undefined;
}

export function parseSearchEngine(referrerUrl: string | undefined): {
  engine?: string;
  query?: string;
} {
  if (!referrerUrl) return {};
  const match = classifySearchEngine(referrerUrl);
  if (!match) return {};
  const out: { engine?: string; query?: string } = { engine: match.engine };
  if (match.queryParam) {
    try {
      const u = new URL(referrerUrl);
      const raw = u.searchParams.get(match.queryParam);
      if (raw) {
        const trimmed = raw.trim();
        if (trimmed && trimmed.length <= SEARCH_QUERY_MAX) out.query = trimmed;
      }
    } catch {
      /* noop */
    }
  }
  return out;
}

// ─── Length capping ───

export function capLength(value: string | undefined, max: number): string | undefined {
  if (!value) return undefined;
  return value.length <= max ? value : undefined;
}

export const LENGTH_CAPS = {
  gclid: GCLID_MAX,
  ga4ClientId: GA4_CID_MAX,
  posthogDistinctId: POSTHOG_ID_MAX,
  posthogSessionId: POSTHOG_SESSION_ID_MAX,
  fbcFbp: FBC_FBP_MAX,
  eventId: EVENT_ID_MAX,
  initialReferrer: REFERRER_MAX,
  searchEngine: SEARCH_ENGINE_MAX,
  searchQuery: SEARCH_QUERY_MAX,
};

// ─── Snapshot ───

export function captureAttribution(): AttributionSnapshot {
  const clickIds = resolveClickIds();
  const adIds = resolveAdIds();
  const initialReferrer = getOrCreateInitialReferrer();
  const search = parseSearchEngine(initialReferrer);
  return {
    utm: resolveUtms(),
    gclid: clickIds.gclid,
    fbc: clickIds.fbc,
    fbp: clickIds.fbp,
    gbraid: clickIds.gbraid,
    wbraid: clickIds.wbraid,
    msclkid: clickIds.msclkid,
    adIds: Object.keys(adIds).length > 0 ? adIds : undefined,
    sessionId: getOrCreateSessionId(),
    landingUrl: getOrCreateLandingUrl(),
    pageUrl: safePageUrl(),
    referrer: safeReferrer(),
    initialReferrer,
    initialSearchEngine: search.engine,
    initialSearchQuery: search.query,
  };
}
